import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE } from '../config/api';
import { 
  Bot, 
  Send, 
  X, 
  Sparkles, 
  CheckCircle2, 
  XCircle,
  Loader2,
  MessageSquare,
  Award,
  ChevronRight,
  User,
  Target,
  RotateCcw,
  Download,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
const API = `${API_BASE}/api`;

// NAVI Avatar URL
const NAVI_AVATAR = "https://customer-assets-7cd3h4nn.emergentagent.net/job_cpd-compass/artifacts/f47qg3qu_image.png";

/**
 * Format NAVI response - removes markdown asterisks and improves readability
 */
const formatNaviResponse = (text) => {
  if (!text) return '';
  
  // Remove all asterisks (bold/italic markdown)
  let formatted = text.replace(/\*+/g, '');
  
  // Clean up any double spaces created
  formatted = formatted.replace(/  +/g, ' ');
  
  // Clean up any leading/trailing whitespace on lines
  formatted = formatted.split('\n').map(line => line.trim()).join('\n');
  
  // Remove excessive newlines (more than 2)
  formatted = formatted.replace(/\n{3,}/g, '\n\n');
  
  return formatted.trim();
};

/**
 * NAVI AI Coach Component
 * Replaces traditional quizzes with AI-driven coaching conversations.
 * Shows pass/fail with objectives analysis and certificate issuance.
 */
export default function NaviCoach({ 
  courseId, 
  moduleId, 
  moduleName,
  token,
  onComplete,
  isOpen,
  onClose,
  isRetake = false
}) {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(5);
  const [assessment, setAssessment] = useState(null);
  const [showAssessment, setShowAssessment] = useState(false);
  const [certificateId, setCertificateId] = useState(null);
  const [waitingForContinue, setWaitingForContinue] = useState(false);
  const [pendingQuestionAdvance, setPendingQuestionAdvance] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current && !sessionComplete) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, sessionComplete]);

  // Start a new coaching session
  const startSession = async () => {
    setIsStarting(true);
    try {
      const response = await axios.post(
        `${API}/navi/coach/start`,
        {
          course_id: courseId,
          module_id: moduleId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSession({
          id: response.data.session_id,
          moduleTitle: response.data.module_title
        });
        setTotalQuestions(response.data.total_questions);
        setMessages([{
          role: 'navi',
          content: response.data.opening_message,
          type: 'greeting'
        }]);
        setCurrentQuestion(1);
      }
    } catch (error) {
      console.error('Failed to start NAVI session:', error);
      toast.error('Failed to start coaching session. Please try again.');
    } finally {
      setIsStarting(false);
    }
  };

  // Send a message to NAVI
  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading || !session || waitingForContinue) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'learner', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API}/navi/coach/chat`,
        {
          session_id: session.id,
          message: userMessage
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const messageType = response.data.message_type;
        
        setMessages(prev => [...prev, {
          role: 'navi',
          content: response.data.response,
          type: messageType
        }]);
        
        // If this is feedback with next question, pause and wait for user to continue
        if (messageType === 'feedback_and_question') {
          setWaitingForContinue(true);
          setPendingQuestionAdvance(response.data.current_question || currentQuestion + 1);
        } else {
          // For other message types, update question immediately
          setCurrentQuestion(response.data.current_question || currentQuestion);
        }
        
        // Update total questions if provided
        if (response.data.total_questions) {
          setTotalQuestions(response.data.total_questions);
        }
        
        if (response.data.session_completed) {
          setSessionComplete(true);
          setWaitingForContinue(false);
          toast.success('NAVI coaching session complete!', {
            description: 'Your performance overview is in the chat above.'
          });
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message. Please try again.');
      setInputValue(userMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle continue after reading feedback
  const handleContinueToNextQuestion = () => {
    if (pendingQuestionAdvance !== null) {
      setCurrentQuestion(pendingQuestionAdvance);
      setPendingQuestionAdvance(null);
    }
    setWaitingForContinue(false);
    // Focus input for next answer
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // Generate assessment
  const generateAssessment = async () => {
    if (!session) return;
    
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/navi/coach/assess`,
        { session_id: session.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setAssessment(response.data.assessment);
        setCertificateId(response.data.certificate_id);
        setShowAssessment(true);
        
        if (response.data.passed) {
          if (response.data.certificate_id && !isRetake) {
            toast.success('Congratulations! You passed!', {
              description: 'Your certificate has been generated.'
            });
          } else if (isRetake) {
            toast.success('You passed the retake!', {
              description: 'Your original certificate remains valid.'
            });
          }
        } else {
          toast.error('Not quite there yet', {
            description: 'Review the feedback and try again.'
          });
        }
        
        if (onComplete) {
          onComplete(response.data.assessment, response.data.passed, response.data.certificate_id);
        }
      }
    } catch (error) {
      console.error('Failed to generate assessment:', error);
      toast.error('Failed to generate assessment.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Reset for retake
  const handleRetake = () => {
    setSession(null);
    setMessages([]);
    setSessionComplete(false);
    setAssessment(null);
    setShowAssessment(false);
    setCurrentQuestion(0);
    setCertificateId(null);
  };

  // Render assessment view
  const renderAssessment = () => {
    if (!assessment) return null;

    const passed = assessment.passed;
    const objectives = assessment.objectives_achieved || [];
    const objectivesSummary = assessment.objectives_summary || {};

    return (
      <div className="p-6 space-y-6 overflow-y-auto max-h-[600px]">
        {/* Pass/Fail Header */}
        <div className={`text-center pb-4 border-b ${passed ? 'border-green-500/30' : 'border-red-500/30'}`}>
          {passed ? (
            <>
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="h-10 w-10 text-green-400" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-green-400">You Passed!</h3>
              {certificateId && !isRetake && (
                <p className="text-green-300 text-sm mt-1 flex items-center justify-center gap-2">
                  <Award className="h-4 w-4" />
                  Certificate Generated
                </p>
              )}
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-3">
                <XCircle className="h-10 w-10 text-red-400" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-red-400">Not Yet Passed</h3>
              <p className="text-zinc-400 text-sm mt-1">
                Review the feedback below and try again
              </p>
            </>
          )}
        </div>

        {/* Overall Score */}
        <div className={`rounded-xl p-4 text-center ${passed ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
          <div className={`text-4xl font-bold ${passed ? 'text-green-400' : 'text-red-400'}`}>
            {assessment.overall_score?.toFixed(1)}/10
          </div>
          <div className="text-sm text-zinc-400 mt-1">Overall Score (6.0 to pass)</div>
        </div>

        {/* Objectives Summary */}
        <div className="bg-zinc-800/50 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-navigate-gold mb-3 flex items-center gap-2">
            <Target className="h-4 w-4" />
            Learning Objectives ({objectivesSummary.achieved || 0}/{objectivesSummary.total || 0} achieved)
          </h4>
          <div className="space-y-2">
            {objectives.map((obj, idx) => (
              <div key={idx} className={`flex items-start gap-3 p-2 rounded-lg ${obj.achieved ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                {obj.achieved ? (
                  <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className="text-sm text-zinc-300">{obj.objective}</p>
                  {obj.evidence && (
                    <p className="text-xs text-zinc-500 mt-1 italic">&ldquo;{obj.evidence}&rdquo;</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Score Breakdown */}
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Understanding', score: assessment.understanding_score },
            { label: 'Application', score: assessment.application_score },
            { label: 'Critical Thinking', score: assessment.critical_thinking_score },
            { label: 'Communication', score: assessment.communication_score }
          ].map((item) => (
            <div key={item.label} className="bg-zinc-800/50 rounded-lg p-3 text-center">
              <div className={`text-xl font-bold ${item.score >= 6 ? 'text-green-400' : 'text-amber-400'}`}>
                {item.score}/10
              </div>
              <div className="text-xs text-zinc-400 mt-1">{item.label}</div>
            </div>
          ))}
        </div>

        {/* Learner Summary */}
        {assessment.learner_summary && (
          <div className="bg-zinc-800/30 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-white mb-2">Your Feedback</h4>
            <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">
              {assessment.learner_summary}
            </p>
          </div>
        )}

        {/* Strengths */}
        {assessment.strengths?.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-green-400 mb-2 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Strengths
            </h4>
            <ul className="space-y-1">
              {assessment.strengths.map((strength, idx) => (
                <li key={idx} className="text-sm text-zinc-300 flex items-start gap-2">
                  <span className="text-green-400 mt-1">+</span>
                  {strength}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Gaps / Areas for Improvement */}
        {(assessment.gaps?.length > 0 || assessment.areas_for_improvement?.length > 0) && (
          <div>
            <h4 className="text-sm font-semibold text-amber-400 mb-2 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {passed ? 'Areas to Strengthen' : 'Gaps to Address'}
            </h4>
            <ul className="space-y-1">
              {[...(assessment.gaps || []), ...(assessment.areas_for_improvement || [])].map((area, idx) => (
                <li key={idx} className="text-sm text-zinc-300 flex items-start gap-2">
                  <span className="text-amber-400 mt-1">!</span>
                  {area}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3 pt-4 border-t border-zinc-700">
          {passed && certificateId && !isRetake && (
            <Button
              onClick={() => navigate('/certificates')}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
            >
              <Download className="h-4 w-4 mr-2" />
              View Certificate
            </Button>
          )}
          
          {!passed && (
            <Button
              onClick={handleRetake}
              className="w-full bg-navigate-gold hover:bg-navigate-gold/90 text-black"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          )}
          
          <Button
            onClick={onClose}
            variant="outline"
            className="w-full border-zinc-600 text-zinc-300"
          >
            Close
          </Button>
        </div>

        {/* Confidence Note */}
        <div className="text-center text-xs text-zinc-500">
          Assessment confidence: {assessment.confidence_level || 'medium'}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
        data-testid="navi-coach-overlay"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-navigate-surface rounded-2xl w-full max-w-lg shadow-2xl border border-zinc-700 overflow-hidden flex flex-col max-h-[90vh]"
          data-testid="navi-coach-modal"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600/20 to-navigate-gold/20 p-4 border-b border-zinc-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img 
                  src={NAVI_AVATAR} 
                  alt="NAVI" 
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <h2 className="font-heading font-bold text-white flex items-center gap-2">
                    Assessment
                    <Sparkles className="h-4 w-4 text-navigate-gold" />
                  </h2>
                  <p className="text-xs text-zinc-400">
                    {moduleName || 'Course Completion Assessment'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-zinc-700 transition-colors"
                data-testid="navi-close-btn"
              >
                <X className="h-5 w-5 text-zinc-400" />
              </button>
            </div>

            {/* Progress indicator */}
            {session && !showAssessment && (
              <div className="mt-3">
                <div className="flex justify-between text-xs text-zinc-400 mb-1">
                  <span>Question {currentQuestion} of {totalQuestions}</span>
                  <span>{Math.round((currentQuestion / totalQuestions) * 100)}%</span>
                </div>
                <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-500 to-navigate-gold transition-all duration-500"
                    style={{ width: `${(currentQuestion / totalQuestions) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Content Area */}
          {showAssessment ? (
            renderAssessment()
          ) : !session ? (
            /* Start Screen */
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500/20 to-navigate-gold/20 flex items-center justify-center mb-6">
                <MessageSquare className="h-10 w-10 text-navigate-gold" />
              </div>
              <h3 className="text-xl font-heading font-bold text-white mb-2">
                {isRetake ? 'Ready to Try Again?' : 'NAVI Coaching Session'}
              </h3>
              <p className="text-zinc-400 text-sm mb-4 max-w-xs">
                I&apos;ll set up a realistic workplace scenario and ask you {totalQuestions} questions to test your understanding.
              </p>
              <div className="bg-zinc-800/50 rounded-lg p-3 mb-6 text-left max-w-xs">
                <p className="text-xs text-zinc-300 mb-2">
                  <span className="text-navigate-gold font-semibold">How it works:</span>
                </p>
                <ul className="text-xs text-zinc-400 space-y-1">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">1.</span>
                    I&apos;ll present a workplace scenario
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">2.</span>
                    Answer my questions based on the scenario
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">3.</span>
                    I&apos;ll provide feedback and correct answers
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">4.</span>
                    Get a summary of what you achieved
                  </li>
                </ul>
              </div>
              <Button
                onClick={startSession}
                disabled={isStarting}
                className="bg-gradient-to-r from-purple-600 to-navigate-gold hover:opacity-90 text-white px-8"
                data-testid="start-navi-session"
              >
                {isStarting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Setting up scenario...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Start Coaching
                  </>
                )}
              </Button>
            </div>
          ) : (
            /* Chat Interface */
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[300px]">
                {messages.map((msg, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.role === 'learner' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`flex items-start gap-2 max-w-[85%] ${
                      msg.role === 'learner' ? 'flex-row-reverse' : ''
                    }`}>
                      {msg.role === 'learner' ? (
                        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-navigate-gold/20">
                          <User className="h-4 w-4 text-navigate-gold" />
                        </div>
                      ) : (
                        <img 
                          src={NAVI_AVATAR} 
                          alt="NAVI" 
                          className="w-8 h-8 rounded-full object-cover shrink-0"
                        />
                      )}
                      <div className={`rounded-2xl px-4 py-3 ${
                        msg.role === 'learner'
                          ? 'bg-navigate-gold/20 text-white'
                          : 'bg-zinc-800 text-zinc-100'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap leading-relaxed">
                          {msg.role === 'learner' ? msg.content : formatNaviResponse(msg.content)}
                        </p>
                        {msg.type === 'question' && (
                          <span className="inline-block mt-2 text-xs text-purple-400 font-medium">
                            Scenario Question
                          </span>
                        )}
                        {msg.type === 'feedback_and_question' && (
                          <span className="inline-block mt-2 text-xs text-blue-400 font-medium">
                            Feedback + Next Question
                          </span>
                        )}
                        {msg.type === 'summary' && (
                          <span className="inline-block mt-2 text-xs text-green-400 font-medium">
                            Performance Overview
                          </span>
                        )}
                        {msg.type === 'probe' && (
                          <span className="inline-block mt-2 text-xs text-amber-400 font-medium">
                            Follow-up
                          </span>
                        )}
                        {msg.type === 'scenario' && (
                          <span className="inline-block mt-2 text-xs text-navigate-gold font-medium">
                            Scenario Setup
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
                
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className="flex items-center gap-2 bg-zinc-800 rounded-2xl px-4 py-3">
                      <Loader2 className="h-4 w-4 text-navigate-gold animate-spin" />
                      <span className="text-sm text-zinc-400">NAVI is thinking...</span>
                    </div>
                  </motion.div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="p-4 border-t border-zinc-700 bg-zinc-900/50">
                {sessionComplete ? (
                  <div className="space-y-3">
                    <p className="text-sm text-zinc-400 text-center">
                      Session complete! Review NAVI&apos;s feedback above.
                    </p>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleRetake}
                        variant="outline"
                        className="flex-1 border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                        data-testid="retake-session-btn"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Try Again
                      </Button>
                      <Button
                        onClick={onClose}
                        className="flex-1 bg-gradient-to-r from-purple-600 to-navigate-gold hover:opacity-90 text-white"
                        data-testid="close-session-btn"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Done
                      </Button>
                    </div>
                  </div>
                ) : waitingForContinue ? (
                  /* Show Continue button after feedback */
                  <div className="space-y-3">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                      <p className="text-sm text-blue-300 text-center">
                        📖 Take a moment to review NAVI&apos;s feedback above
                      </p>
                    </div>
                    <Button
                      onClick={handleContinueToNextQuestion}
                      className="w-full bg-gradient-to-r from-purple-600 to-navigate-gold hover:opacity-90 text-white"
                      data-testid="continue-to-next-btn"
                    >
                      <ChevronRight className="h-4 w-4 mr-2" />
                      Continue to Next Question
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <textarea
                      ref={inputRef}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your response..."
                      rows={2}
                      className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-navigate-gold/50 resize-none text-sm"
                      disabled={isLoading}
                      data-testid="navi-chat-input"
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={!inputValue.trim() || isLoading}
                      className="bg-navigate-gold hover:bg-navigate-gold/90 text-black h-auto"
                      data-testid="navi-send-btn"
                    >
                      {isLoading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Send className="h-5 w-5" />
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
